import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs/operators';
import { DistributedLogService } from './services/distributed-log.service';

/**
 * Distributed-logging *linkage* interceptor.
 *
 * Attaches the session `traceId` and a per-request `correlationId` to every
 * outgoing request (as `X-Trace-Id` / `X-Correlation-Id`), so the server can
 * correlate its logs with the client's. It also records the request outcome back
 * into the {@link DistributedLogService} buffer for linkage.
 *
 * Requests to our own sink are skipped so we don't log the log.
 */
export const distributedLogInterceptor: HttpInterceptorFn = (req, next) => {
  // Do not attach custom headers to external storage / signed upload URLs
  if (
    req.url.includes('storage.googleapis.com') ||
    req.url.includes('googleapis.com/storage') ||
    req.url.includes('amazonaws.com') ||
    req.url.includes('blob.core.windows.net')
  ) {
    return next(req);
  }

  const dl = inject(DistributedLogService);
  if (!dl.enabled || dl.isSinkRequest(req.url)) {
    return next(req);
   }

  const correlationId = dl.newCorrelationId();
  const started = Date.now();

  const traced = req.clone({
    setHeaders: {
      'X-Trace-Id': dl.traceIdValue,
      'X-Correlation-Id': correlationId,
     },
   });

  return next(traced).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          dl.recordHttp({
            method: req.method,
            url: req.url,
            correlationId,
            status: event.status,
            ok: true,
            ms: Date.now() - started,
          });
        }
      },
      error: (err: HttpErrorResponse | Error) => {
        const status = err instanceof HttpErrorResponse ? err.status : 0;
        dl.recordHttp({
          method: req.method,
          url: req.url,
          correlationId,
          status,
          ok: false,
          ms: Date.now() - started,
          error: err,
         });
        },
     })
   );
};
